import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import { Store } from '@ngxs/store';
import {Observable} from 'rxjs';
import {endpoints} from 'src/app/shared/constants/endpoints';
import { PharmacyState } from '../shared/states/pharmacy/pharmacy.state';
import { IScheduleFilter, schedulesList, schedulesListWithCompound } from '../shared/interfaces/IScheduleFilter';
import { downloadFile } from '../shared/helpers/helpers';
import { IExportCurrentInventoryReportRequest } from './interfaces/export-current-inventory-report-request.interface';
import { GroupedBy } from '../shared/interfaces/grouped-by.enum';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {

  constructor(private http: HttpClient, private store: Store) {
  }

  downloadCustomReport(data: any): Observable<Blob> {
    return this.http.post(endpoints.report.custom.downloadCsv, data, { responseType: 'blob'});
  }

  downloadCustomPDFReport(data: any): Observable<Blob> {
    return this.http.post(endpoints.report.custom.downloadPdf, data, { responseType: 'blob'});
  }

  downloadSohHistoryPDFReport(data: any): Observable<Blob> {
    return this.http.post(endpoints.report.soh.downloadPdf, data, { responseType: 'blob'});
  }

  downloadSohHistoryCsvReport(data: any): Observable<Blob> {
    return this.http.post(endpoints.report.soh.downloadCsv, data, { responseType: 'blob'});
  }

  downloadNdcPurchaseCsvReport(data: any): Observable<Blob> {
    return this.http.post(endpoints.report.ndcPurchase.downloadCsv, data, { responseType: 'blob'});
  }

  downloadNdcPurchasePdfReport(data: any): Observable<Blob> {
    return this.http.post(endpoints.report.ndcPurchase.downloadPdf, data, { responseType: 'blob'});
  }

  downloadDeaCsvReport(reportId: string, sortBy: string | null, ascending: boolean | null, withLocations: boolean, groupedBy: GroupedBy): Observable<Blob> {
    return this.http.get(`${endpoints.report.dea.downloadCsv}/${reportId}`,
      {responseType: 'blob', params: this._getDeaReportParameters(sortBy, ascending, withLocations, groupedBy)});
  }

  downloadDeaPdfReport(reportId: string, sortBy: string | null, ascending: boolean | null, withLocations: boolean, groupedBy: GroupedBy): Observable<Blob> {
    return this.http.get(`${endpoints.report.dea.downloadPdf}/${reportId}`,
      {responseType: 'blob', params: this._getDeaReportParameters(sortBy, ascending, withLocations, groupedBy)});
  }

  _getDeaReportParameters(sortColumn: string | null, ascending: boolean | null, withLocations: boolean, groupedBy: GroupedBy): HttpParams {
    var queryParams = new HttpParams();
    queryParams = queryParams.appendAll({sortBy: sortColumn, ascending: ascending ? 'True' : 'False', withLocations: withLocations ? 'True' : 'False', groupedBy});
    return queryParams;
  }

  downloadInventoryCSVTotalsReport(payload: any): Observable<Blob> {
    return this.http.post(endpoints.report.inventoryTotals.downloadCsv, payload, { responseType: 'blob'});
  }
  downloadInventoryPDFTotalsReport(payload: any): Observable<Blob> {
    return this.http.post(endpoints.report.inventoryTotals.downloadPdf, payload, { responseType: 'blob'});
  }

  downloadCurrentInventoryPdfReport(request: IExportCurrentInventoryReportRequest): Observable<Blob> {
    return this.http.post(endpoints.report.currentInventory.exportPdf, request, { responseType: 'blob'});
  }

  downloadCurrentInventoryCsvReport(request: IExportCurrentInventoryReportRequest): Observable<Blob> {
    return this.http.post(endpoints.report.currentInventory.exportCsv, request, { responseType: 'blob'});
  }

  composeReportTitleWithLocationsExtension(title, locations: string[]): string {
    if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled) && locations?.length) {
      return title + ` in ${locations.reduce((accumulator, currentValue) => accumulator + ', ' + currentValue)} Location${locations.length > 1 ? 's' : ''}`;
    }
  }

  composeReportTitleWithScheduleExtension(title, schedules: number[]): string {
    if (this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs) && schedules?.length) {
      let filtered: IScheduleFilter[] = [];
      if (!this.store.selectSnapshot(PharmacyState.compoundingEnabled)) {
        filtered = schedulesList.filter(x => schedules.includes(x.id));
      } else filtered = schedulesListWithCompound.filter(x => schedules.includes(x.id));
      
      return title + ` (${filtered.sort((a, b) => a.id - b.id).map(x => x?.badgeAlias)?.reduce((accumulator, currentValue) => accumulator + ', ' + currentValue)})`;
    }
    return title;
  }

  handleDownload(res: Blob, fileName: string): void {
    downloadFile(res, fileName);
  }
}
